import { useEffect, useRef, useState } from 'react';
import { motion } from "framer-motion";

import './AddressInput.css';
import Input from '../../../Input/Input';
import MiniPreloader from '../../../MiniPreloader/MiniPreloader';
import mainApi from '../../../../assets/api/MainApi';

const DEBOUNCE_TIME = 1500;

const dropdownVariants = {
  open: {
    clipPath: "inset(0% 0% 0% 0%)",
    transition: {
      type: "spring",
      bounce: 0,
      duration: 0.5,
      delayChildren: 0.2,
      staggerChildren: 0.05,
    },
  },
  closed: {
    clipPath: "inset(0 90% 90% 10% round 8px)",
    transition: {
      type: "spring",
      bounce: 0,
      duration: 0.3,
    },
  },
};

function AddressInput({ form, setForm, error, hint }) {
  const dropRef = useRef();
  const [preloaders, setPreloaders] = useState({
    hints: false,
    geo: false, // прелоадер получения координат
  });

  const [addressHints, setAddressHints] = useState(null);
  const [isDropOpen, setDropOpen] = useState(false);

  function handleFocus() {
    setDropOpen(addressHints && addressHints.length > 0);
  }

  // дебаунс на подсказки адреса
  useEffect(() => {
    const address = form.address;
    if (!address || address.length <= 3 || address === form.hint_selected) return;

    const delayDebounceFn = setTimeout(() => {
      setPreloaders(prev => ({ ...prev, hints: true }));
      mainApi
        .searchAddress({ query: address })
        .then((res) => {
          setAddressHints(res.data);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setPreloaders(prev => ({ ...prev, hints: false }));
        });
    }, DEBOUNCE_TIME);

    return () => clearTimeout(delayDebounceFn);
  }, [form.address, form.hint_selected]);

  // выбор подсказки: фиксируем текст и тянем координаты
  async function handleHintClick(hintValue) {
    setPreloaders(prev => ({ ...prev, hints: true }));
    setForm(prev => ({
      ...prev,
      hint_selected: hintValue,
      address: hintValue,        // зафиксируем в поле адрес тоже
      client_coords: undefined,  // сбросим предыдущие координаты клиента
    }));

    try {
      // обновим подсказки под выбранный hint (не обязательно, но оставлю как было)
      const hintsRes = await mainApi.searchAddress({ query: hintValue }).catch(() => null);
      if (hintsRes?.data) setAddressHints(hintsRes.data);

      // теперь получаем координаты
      setPreloaders(prev => ({ ...prev, geo: true }));
      const coordsRes = await mainApi.getCoordinates({ address: hintValue });
      // ожидаем { lon, lat }
      const { lon, lat } = coordsRes || {};
      if (
        typeof lon === "number" &&
        typeof lat === "number" &&
        !Number.isNaN(lon) &&
        !Number.isNaN(lat)
      ) {
        // сохраняем как [lon, lat] — MapEmbed ожидает именно такой порядок
        setForm(prev => ({
          ...prev,
          client_coords: [lon, lat],
        }));
      } else {
        console.warn("Invalid coordinates from API:", coordsRes);
        setForm(prev => ({ ...prev, client_coords: undefined }));
      }
    } catch (e) {
      console.log(e);
      setForm(prev => ({ ...prev, client_coords: undefined }));
    } finally {
      setPreloaders(prev => ({ ...prev, hints: false, geo: false }));
      setDropOpen(false);
      setSelectedHintIndex(-1);
    }
  }

  useEffect(() => {
    setDropOpen(addressHints && addressHints.length > 0);
  }, [addressHints]);

  useEffect(() => {
    function handleOutsideClickClose(evt) {
      if (isDropOpen && dropRef.current && !dropRef.current.contains(evt.target)) {
        setDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClickClose);
    return () => document.removeEventListener("mousedown", handleOutsideClickClose);
  });

  const [selectedHintIndex, setSelectedHintIndex] = useState(-1);

  function handleKeyDown(e) {
    if (!addressHints || addressHints.length === 0) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setDropOpen(false);
      if (document.activeElement.tagName === 'INPUT') {
        document.activeElement.blur();
      }
      setSelectedHintIndex(-1);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedHintIndex(prevIndex =>
        prevIndex < addressHints.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedHintIndex(prevIndex =>
        prevIndex > 0 ? prevIndex - 1 : addressHints.length - 1
      );
    } else if (e.key === 'Enter') {
      if (selectedHintIndex >= 0 && selectedHintIndex < addressHints.length) {
        handleHintClick(addressHints[selectedHintIndex]);
        setDropOpen(false);
        setSelectedHintIndex(-1);
      }
    }
  }

  function handleAddressChange(e) {
    const input = e.target;
    let value = input.value;
    const name = input.name;

    switch (name) {
      case 'address': {
        if (value.length > 3) {
          setPreloaders(prev => ({ ...prev, hints: true }));
        } else {
          setPreloaders(prev => ({ ...prev, hints: false }));
        }
        setForm(prev => ({
          ...prev,
          [name]: value,
          hint_selected: '',
          client_coords: undefined, // при ручном вводе — сбрасываем координаты
        }));
        setAddressHints(null);
        break;
      }

      default: {
        setForm(prev => ({
          ...prev,
          [name]: value,
        }));
        break;
      }
    }
  }

  return (
    <div className='address-input' ref={dropRef}>
      <div className="personal-info__input">
        <Input
          label={"Улица, дом*"}
          value={form.hint_selected ? form.hint_selected : form.address}
          handleChange={handleAddressChange}
          name={"address"}
          onKeyDown={handleKeyDown}
          handleFocus={handleFocus}
          preloader={preloaders.hints || preloaders.geo}
          isCorrectState={Boolean(form.hint_selected)}
          notCorrectAddress={error}
          error={error ? { errorMessage: 'К&nbsp;сожалению, доставка по&nbsp;указанному адресу недоступна' } : ''}
          hint={hint ? `Приблизительное время доставки: ${hint}` : null}
        />
      </div>

      <motion.div
        variants={dropdownVariants}
        className='address-input__hints'
        initial={false}
        animate={isDropOpen ? "open" : "closed"}
      >
        {
          !preloaders.hints
            ? addressHints && addressHints.length > 0
              ? addressHints.map((h, i) => (
                  <button
                    onClick={() => handleHintClick(h)}
                    className={`address-input__hint ${selectedHintIndex === i ? 'address-input__hint_selected' : ''}`}
                    type='button'
                    key={`address-input__hint-${h}-${i}`}
                  >
                    {h}
                  </button>
                ))
              : null
            :
            <MiniPreloader />
        }
      </motion.div>
    </div>
  );
}

export default AddressInput;
